import { useNavigate, useLocation } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { useMapActionsStore } from '@/store/mapActionsStore';
import {
  Car,
  Store,
  Flame,
  AlertTriangle,
  Info,
  MapPin,
  ChevronRight,
} from 'lucide-react';
import type {
  Finding,
  FindingKind,
  FindingMetric,
  FindingTone,
} from '@/types/askAi';

const KIND_ICON: Record<FindingKind, typeof Car> = {
  vehicle: Car,
  merchant: Store,
  hotspot: Flame,
  alert: AlertTriangle,
  summary: Info,
};

const KIND_ACCENT: Record<FindingKind, string> = {
  vehicle: 'text-blue-700',
  merchant: 'text-amber-700',
  hotspot: 'text-orange-700',
  alert: 'text-red-700',
  summary: 'text-slate-600',
};

const TONE_CHIP: Record<FindingTone, string> = {
  critical: 'bg-red-100 text-red-800 border-red-200',
  warn: 'bg-amber-100 text-amber-800 border-amber-200',
  ok: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  muted: 'bg-slate-100 text-slate-700 border-slate-200',
};

function MetricChip({ metric }: { metric: FindingMetric }) {
  const tone: FindingTone = metric.tone ?? 'muted';
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] font-medium',
        TONE_CHIP[tone],
      )}
    >
      <span className="opacity-70">{metric.label}</span>
      <span>{metric.value}</span>
    </span>
  );
}

interface FindingCardProps {
  finding: Finding;
}

export function FindingCard({ finding }: FindingCardProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const pushActions = useMapActionsStore((s) => s.pushActions);

  const Icon = KIND_ICON[finding.kind] ?? Info;
  const hasCoords = finding.lng != null && finding.lat != null;

  const onCardClick = () => {
    if (!hasCoords) return;
    pushActions([
      {
        action: 'drop_pin',
        lng: finding.lng!,
        lat: finding.lat!,
        label: finding.title,
      },
      {
        action: 'zoom_to',
        lng: finding.lng!,
        lat: finding.lat!,
        zoom: 13,
      },
    ]);
    if (!location.pathname.startsWith('/maps')) {
      navigate('/maps');
    }
  };

  return (
    <Card
      onClick={hasCoords ? onCardClick : undefined}
      className={cn(
        'overflow-hidden border bg-background/80 p-2.5 text-xs shadow-none',
        hasCoords && 'cursor-pointer transition-colors hover:border-primary/60 hover:bg-accent/40',
      )}
    >
      <div className="flex items-start gap-2">
        <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', KIND_ACCENT[finding.kind])} />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold leading-tight">
                {finding.title}
              </div>
              {finding.subtitle && (
                <div className="truncate text-[11px] text-muted-foreground">
                  {finding.subtitle}
                </div>
              )}
            </div>
            {hasCoords && (
              <span className="flex shrink-0 items-center gap-0.5 text-[10px] text-muted-foreground">
                <MapPin className="h-3 w-3" />
                <ChevronRight className="h-3 w-3" />
              </span>
            )}
          </div>

          {finding.metrics && finding.metrics.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {finding.metrics.map((m, i) => (
                <MetricChip key={`${m.label}-${i}`} metric={m} />
              ))}
            </div>
          )}

          {finding.links && finding.links.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {finding.links.map((l, i) => (
                <button
                  key={`${l.href}-${i}`}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(l.href);
                  }}
                  className="rounded border border-input bg-background px-2 py-0.5 text-[11px] text-primary hover:bg-accent"
                >
                  {l.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

export default FindingCard;
